// @flow
import { fetchLiveCatalogRegistrationIfNeeded, fetchUpcomingCatalogRegistrationIfNeeded } from './catalogRegistration';
import { formatDistance, parseISO } from 'date-fns';
import { getDeployment, getPushSubscriptionPublicKey } from './config';
import { getImageUrl, urlSafeTitle } from '../../utils/urls';
import api from '../api/notifications';
import { createSelector } from 'reselect';
import { fetchBidLimitBalanceIfNeeded } from './bidLimit';
import { getAuthToken } from './user';
import { getCatalog } from './catalog';
import type { GlobalState } from '../rootReducer';
import itemApi from '../api/item';
import { LOG_OUT_BIDDER } from './actions';
import ms from 'ms';
import notificationPermissions from '../../enums/notificationPermissions';
import { OPEN_DOWNLOAD_MOBILE_MODAL } from './modal';
import type { PubnubMessage } from '../../types/pubnub';
import uuid from 'uuid';

/* Action Types */
export const LOAD_NOTIFICATIONS_FAIL = 'la/domain/notifications/LOAD_FAIL';
export const LOAD_NOTIFICATIONS_REQUEST = 'la/domain/notifications/LOAD_REQUEST';
export const LOAD_NOTIFICATIONS_SUCCESS = 'la/domain/notifications/LOAD_SUCCESS';

export const MARK_DELETED_FAIL = 'la/domain/notification/MARK_DELETED_FAIL';
export const MARK_DELETED_REQUEST = 'la/domain/notification/MARK_DELETED_REQUEST';
export const MARK_DELETED_SUCCESS = 'la/domain/notification/MARK_DELETED_SUCCESS';

export const MARK_READ_FAIL = 'la/domain/notification/MARK_READ_FAIL';
export const MARK_READ_REQUEST = 'la/domain/notification/MARK_READ_REQUEST';
export const MARK_READ_SUCCESS = 'la/domain/notification/MARK_READ_SUCCESS';

export const PUBNUB_BIDDER_NOTIFICATION_RECEIVED = 'la/domain/notification/PUBNUB_BIDDER_NOTIFICATION_RECEIVED';

export const SET_PERMISSION = 'la/domain/notification/SET_PERMISSION';
export const SET_PERMISSION_OVERLAY = 'la/domain/notification/SET_PERMISSION_OVERLAY';

export const SET_SUBSCRIPTION_FAIL = 'la/domain/notifications/SET_SUBSCRIPTION_FAIL';
export const SET_SUBSCRIPTION_REQUEST = 'la/domain/notifications/SET_SUBSCRIPTION_REQUEST';
export const SET_SUBSCRIPTION_SUCCESS = 'la/domain/notifications/SET_SUBSCRIPTION_SUCCESS';

export const DISABLE_SUBSCRIPTION_FAIL = 'la/domain/notifications/DISABLE_SUBSCRIPTION_FAIL';
export const DISABLE_SUBSCRIPTION_REQUEST = 'la/domain/notifications/DISABLE_SUBSCRIPTION_REQUEST';
export const DISABLE_SUBSCRIPTION_SUCCESS = 'la/domain/notifications/DISABLE_SUBSCRIPTION_SUCCESS';

export const FORCE_ENABLE_NOTIFICATIONS_BANNER = 'la/domain/notifications/FORCE_ENABLE_NOTIFICATIONS_BANNER';
export const FORCE_ENABLE_NOTIFICATIONS_BANNER_DISMISS =
    'la/domain/notifications/FORCE_ENABLE_NOTIFICATIONS_BANNER_DISMISS';

const ERROR_TIMEOUT = ms('1m');

/* reducer */
const DEFAULT_STATE = {
    errorTime: 0,
    forceEnableBanner: false,
    hasActiveSubscription: false,
    hasNewNotifications: true,
    loading: false,
    notificationCount: 0,
    notifications: [],
    permission: '',
    pushNotifications: [],
    showPermissionOverlay: false,
};

export type State = {|
    ...typeof DEFAULT_STATE,
|};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    let filteredNotifications;
    switch (action.type) {
        case LOAD_NOTIFICATIONS_SUCCESS:
            return {
                ...state,
                errorTime: 0,
                hasNewNotifications: false,
                loading: false,
                notificationCount: [...action.payload.filter(x => !x.isRead)].length,
                notifications: [...action.payload],
            };
        case MARK_DELETED_SUCCESS:
            // $FlowFixMe
            filteredNotifications = state.notifications.filter(x => !action.payload.includes(x.id));
            return {
                ...state,
                notificationCount: state.notificationCount,
                notifications: filteredNotifications,
            };
        case MARK_READ_SUCCESS:
            return {
                ...state,
                notificationCount: state.notificationCount - action.payload.length,
            };
        case PUBNUB_BIDDER_NOTIFICATION_RECEIVED:
            return {
                ...state,
                hasNewNotifications: true,
                pushNotifications: [...state.pushNotifications, action.payload],
            };
        case SET_PERMISSION:
            return {
                ...state,
                permission: action.payload,
                pushNotifications: [],
            };
        case SET_PERMISSION_OVERLAY:
            return {
                ...state,
                showPermissionOverlay: action.payload,
            };
        case SET_SUBSCRIPTION_SUCCESS:
            return {
                ...state,
                hasActiveSubscription: true,
            };
        case DISABLE_SUBSCRIPTION_SUCCESS:
            return {
                ...state,
                hasActiveSubscription: false,
            };
        case LOAD_NOTIFICATIONS_FAIL:
            return {
                ...state,
                errorTime: action.meta.actionTime,
                loading: false,
            };
        case LOAD_NOTIFICATIONS_REQUEST:
            return {
                ...state,
                loading: true,
            };
        case FORCE_ENABLE_NOTIFICATIONS_BANNER:
            return {
                ...state,
                forceEnableBanner: true,
            };
        case FORCE_ENABLE_NOTIFICATIONS_BANNER_DISMISS:
            return {
                ...state,
                forceEnableBanner: false,
            };
        case LOG_OUT_BIDDER:
            return DEFAULT_STATE;
        case MARK_DELETED_FAIL:
        case MARK_DELETED_REQUEST:
        case MARK_READ_FAIL:
        case MARK_READ_REQUEST:
        case SET_SUBSCRIPTION_FAIL:
        case SET_SUBSCRIPTION_REQUEST:
        case DISABLE_SUBSCRIPTION_FAIL:
        case DISABLE_SUBSCRIPTION_REQUEST:
            return state;
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.notification;
const userStateSelector = (state: GlobalState) => state.user;

export const getNotifications = createSelector(stateSelector, notification => notification.notifications || []);

export const getNotificationCount = createSelector(stateSelector, notification => notification.notificationCount || 0);

export const getHasNewNotifications = createSelector(
    stateSelector,
    notification => notification.hasNewNotifications || false
);

export const getPushNotifications = createSelector(stateSelector, notification => notification.pushNotifications);

export const getPermission = createSelector(stateSelector, notification => notification.permission);

export const getShowPermissionsOverlay = createSelector(
    stateSelector,
    notification => notification.showPermissionOverlay
);

export const getHasActiveSubscription = createSelector(
    stateSelector,
    notification => notification.hasActiveSubscription
);

export const getIsLoading = createSelector(stateSelector, notification => notification.loading);

export const getNeedsSubscription = createSelector(
    [userStateSelector, getHasActiveSubscription, getPermission],
    (userState, hasActiveSubscription, permission) =>
        !hasActiveSubscription && permission === notificationPermissions.GRANTED && Boolean(userState.token)
);

export const getErrorTime = createSelector(stateSelector, notification => notification.errorTime || 0);

export const getForceEnableBanner = createSelector(stateSelector, notification => notification.forceEnableBanner);

/* ACTION CREATORS */
export const createPubnubBidderNotificationsOnMessageReceived = (originalBidderId: number) => (
    message: PubnubMessage
) => async (dispatch: Function, getState: Function) => {
    const { message: msg } = message;

    if (msg.bidderId === originalBidderId) {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        let notification;
        if (msg.itemId) {
            const itemIds = [msg.itemId];
            const item = await itemApi.fetchItemsByIds({ authToken, deployment, itemIds });
            notification = transformPushNotificationWithItems(msg, item);
        } else if (msg.notificationType === 'registrationChangedData') {
            dispatch(updateRegistrationStatus(msg.catalogId));
        } else {
            notification = transformPushNotification(msg);
        }
        dispatch({
            payload: notification,
            type: PUBNUB_BIDDER_NOTIFICATION_RECEIVED,
        });
    }
};

export const updateRegistrationStatus = (catalogId: number) => async (dispatch: Function, getState: Function) => {
    if (catalogId) {
        return Promise.all([
            dispatch(fetchUpcomingCatalogRegistrationIfNeeded(catalogId)),
            dispatch(fetchBidLimitBalanceIfNeeded(catalogId)),
        ]).then(() => {
            const state = getState();
            const catalog = getCatalog(state, catalogId);
            if (catalog?.catalogStatus === 'live') {
                // adding a little delay to ensure that dante has had a chance to update after getting the same notification
                setTimeout(() => dispatch(fetchLiveCatalogRegistrationIfNeeded(catalogId)), 1500);
            }
        });
    }
    return Promise.resolve();
};

export const loadNotifications = () => async (dispatch: Function, getState: Function) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        if (!authToken) {
            return;
        }
        dispatch({
            type: LOAD_NOTIFICATIONS_REQUEST,
        });
        const notifications = await api.fetchNotifications({ authToken, deployment });
        const itemIds = notifications.payload.map(x => x.itemId).filter(x => x);
        let items = { data: { items: [] } };
        if (itemIds.length) {
            items = await itemApi.fetchItemsByIds({ authToken, deployment, itemIds });
        }
        const transformedNotifications = transformNotificationsWithItems(notifications.payload, items.data.items);
        dispatch({
            payload: transformedNotifications,
            type: LOAD_NOTIFICATIONS_SUCCESS,
        });
    } catch (error) {
        dispatch({
            error: true,
            meta: { actionTime: Date.now() },
            payload: error,
            type: LOAD_NOTIFICATIONS_FAIL,
        });
    }
};

const shouldFetchNotifications = state => {
    const token = getAuthToken(state);
    const loading = getIsLoading(state);
    const hasNewNotifications = getHasNewNotifications(state);
    const errorTime = getErrorTime(state);
    let errorLimit = false;
    if (Boolean(errorTime)) {
        const diff = Date.now() - errorTime;
        errorLimit = diff <= ERROR_TIMEOUT;
    }
    return Boolean(token) && !loading && Boolean(hasNewNotifications) && !errorLimit;
};

export const fetchNotificationsIfNeeded = () => (dispatch: Function, getState: Function) => {
    if (shouldFetchNotifications(getState())) {
        return dispatch(loadNotifications());
    }
    return Promise.resolve();
};

export const forceEnableNotificationsBanner = () => (dispatch: Function, getState: Function) => {
    dispatch({
        type: FORCE_ENABLE_NOTIFICATIONS_BANNER,
    });
    return Promise.resolve();
};

export const forceEnableNotificationsBannerDismiss = () => (dispatch: Function, getState: Function) => {
    dispatch({
        type: FORCE_ENABLE_NOTIFICATIONS_BANNER_DISMISS,
    });
    return Promise.resolve();
};

export const markNotificationsDeleted = (notificationIds: any) => async (dispatch: Function, getState: Function) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        if (!authToken) {
            return;
        }
        dispatch({
            payload: notificationIds,
            type: MARK_DELETED_REQUEST,
        });
        await api.postMarkDeleted({ authToken, deployment, notificationIds });
        dispatch({
            payload: notificationIds,
            type: MARK_DELETED_SUCCESS,
        });
    } catch (error) {
        dispatch({
            error: true,
            payload: error,
            type: MARK_DELETED_FAIL,
        });
    }
};

export const markNotificationsRead = (notificationIds: any) => async (dispatch: Function, getState: Function) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        if (!authToken) {
            return;
        }
        dispatch({
            payload: notificationIds,
            type: MARK_READ_REQUEST,
        });
        await api.postMarkRead({ authToken, deployment, notificationIds });
        dispatch({
            payload: notificationIds,
            type: MARK_READ_SUCCESS,
        });
    } catch (error) {
        dispatch({
            error: true,
            payload: error,
            type: MARK_READ_FAIL,
        });
    }
};

export const setPermission = (permission: any) => async (dispatch: Function) => {
    if (!permission) {
        dispatch(disableWebPushSubscription());
    }
    dispatch({
        payload: permission,
        type: SET_PERMISSION,
    });
};

export const openMobileDownloadModal = () => (dispatch: Function) => {
    dispatch({
        type: OPEN_DOWNLOAD_MOBILE_MODAL,
    });
};

export const getBrowserPermission = () => async (dispatch: Function, getState: Function) => {
    if (!window || !window.Notification) {
        dispatch({
            payload: notificationPermissions.UNSUPPORTED,
            type: SET_PERMISSION,
        });
    } else {
        dispatch({
            payload: Notification.permission,
            type: SET_PERMISSION,
        });
        if (Notification.permission === notificationPermissions.GRANTED) {
            dispatch(configureWebPushSubscription());
        }
    }
};

export const requestBrowserPermission = () => async (dispatch: Function, getState: Function) => {
    if (!window.Notification) {
        //browser doesn't support notifications
        dispatch({
            payload: notificationPermissions.UNSUPPORTED,
            type: SET_PERMISSION,
        });
    } else {
        dispatch({
            payload: true,
            type: SET_PERMISSION_OVERLAY,
        });
        Notification.requestPermission(async reply => {
            if (reply === notificationPermissions.GRANTED) {
                sendInitialNotification();
                dispatch(configureWebPushSubscription());
            }
            dispatch({
                payload: reply,
                type: SET_PERMISSION,
            });
            dispatch({
                payload: false,
                type: SET_PERMISSION_OVERLAY,
            });
        });
    }
};

export const configureWebPushSubscription = () => async (dispatch: Function, getState: Function) => {
    try {
        if (supportsPushNotifications()) {
            dispatch({ type: SET_SUBSCRIPTION_REQUEST });
            const state = getState();
            const authToken = getAuthToken(state);
            const deployment = getDeployment(state);
            const key = getPushSubscriptionPublicKey(state);
            const subscription = await updateSubscription(key);
            if (!authToken) return;
            await api.postUpdateWebPushSubscription({ authToken, deployment, subscription });
            dispatch({ type: SET_SUBSCRIPTION_SUCCESS });
        }
    } catch (e) {
        dispatch({
            type: SET_SUBSCRIPTION_FAIL,
        });
    }
};

const disableWebPushSubscription = () => async (dispatch: Function, getState: Function) => {
    try {
        dispatch({ type: DISABLE_SUBSCRIPTION_REQUEST });
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        await api.postDisableWebPushSubscription({ authToken, deployment });
        dispatch({ type: DISABLE_SUBSCRIPTION_SUCCESS });
    } catch (e) {
        dispatch({
            type: DISABLE_SUBSCRIPTION_FAIL,
        });
    }
};

const transformTimeStamp = date => {
    const time = parseISO(date);
    const now = new Date();
    return formatDistance(time, now, {
        addSuffix: true,
    });
};

export const transformLink = (link: any) => {
    let usableLink = link;
    usableLink = link.includes('/item/') ? `/item/${urlSafeTitle(link.split('/item/').pop())}` : usableLink;
    usableLink = link.includes('/console/') ? `/console/${urlSafeTitle(link.split('/console/').pop())}` : usableLink;
    usableLink = link.includes('/catalog/') ? `/catalog/${urlSafeTitle(link.split('/catalog/').pop())}` : usableLink;
    return usableLink;
};

const transformPushNotification = message => {
    let notification = message;
    notification.id = uuid.v4();
    return notification;
};

const sendInitialNotification = () => {
    new Notification('From LiveAuctioneers', {
        body: 'Your notifications are enabled!',
    });
};

const transformNotificationsWithItems = (notifications, items) => {
    notifications.forEach(n => {
        if (n.itemId) {
            const item = items.filter(i => i.itemId === n.itemId)[0];
            let imageUrl = '';
            if (item) {
                const { catalogId, imageVersion, itemId, sellerId } = item;
                const imageOptions: any = {
                    catalogId,
                    imageNumber: 1,
                    imageVersion,
                    itemId,
                    sellerId,
                };
                imageUrl = getImageUrl(imageOptions);
            }
            n.imageUrl = imageUrl;
        }
        n.link = transformLink(n.link);
        n.timestamp = transformTimeStamp(n.createdAt);
    });
    return notifications;
};

const transformPushNotificationWithItems = (message, item) => {
    let notification = message;
    let imageUrl = '';
    if (item && item.data && item.data.items && item.data.items[0]) {
        const { catalogId, imageVersion, itemId, sellerId } = item.data.items[0];
        const imageOptions: any = {
            catalogId,
            imageNumber: 1,
            imageVersion,
            itemId,
            sellerId,
        };
        imageUrl = getImageUrl(imageOptions);
    }
    notification.imageUrl = imageUrl;
    notification.id = uuid.v4();
    return notification;
};

const supportsPushNotifications = () => {
    return !(!navigator || !window || !('serviceWorker' in navigator) || !('PushManager' in window));
};
const registerServiceWorker = async () => {
    // $FlowFixMe
    const swRegistration = await navigator.serviceWorker.register('../../pushNotificationServiceWorker.js');
    return swRegistration;
};

const updateSubscription = async subscriptionKey => {
    const swRegistration = await registerServiceWorker();
    const applicationServerKey = urlBase64ToUint8Array(subscriptionKey);
    const options = { applicationServerKey, userVisibleOnly: true };
    const subscription = await swRegistration.pushManager.subscribe(options);
    const sub = subscription.toJSON();
    const newSub = {
        endpoint: sub.endpoint,
        keys: sub.keys,
    };
    return newSub;
};

// urlBase64ToUint8Array is a magic function that will encode the base64 public key
// to Array buffer which is needed by the subscription option
const urlBase64ToUint8Array = base64String => {
    var padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    var base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

    var rawData = window.atob(base64);
    var outputArray = new Uint8Array(rawData.length);

    for (var i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
};
